import React, { useEffect, useState } from 'react';

const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'portfolio', label: 'Work' },
  { id: 'resume', label: 'Experience' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
];

const Header = ({ data }) => {
  const [scrollCueHidden, setScrollCueHidden] = useState(false);
  const [navOpaque, setNavOpaque] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  // Scroll cue, nav background and current-section highlight. This replaced the
  // jQuery/waypoints init.js the Ceevee template shipped with.
  useEffect(() => {
    const heroEl = document.getElementById('home');
    let frame = null;

    const measure = () => {
      frame = null;
      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        // The cue sits at the bottom of the hero, so retire it once the hero is
        // mostly gone rather than waiting for it to leave entirely.
        setScrollCueHidden(
          rect.bottom < Math.min(140, window.innerHeight * 0.18),
        );
        setNavOpaque(window.scrollY >= rect.height * 0.2);
      }

      // Mirrors waypoints' 35% offset: whichever section owns that scanline wins.
      const line = window.scrollY + window.innerHeight * 0.35;
      let current = NAV_ITEMS[0].id;
      for (const item of NAV_ITEMS) {
        const el = document.getElementById(item.id);
        if (el && el.offsetTop <= line) current = item.id;
      }
      setActiveSection(current);
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const name = data?.name || '';
  const occupation = data?.occupation || '';
  const description = data?.description || '';
  const headline = data?.headline || '';
  // const subheadline = data?.subheadline || '';
  const city = data?.address?.city || '';
  const proofStrip = Array.isArray(data?.proofStrip) ? data.proofStrip : [];

  const social = Array.isArray(data?.social) ? data.social : [];
  const networks = social.map(function (network) {
    return (
      <li key={network.name}>
        <a
          href={network.url}
          aria-label={network.name}
          target={network.url?.startsWith('mailto:') ? undefined : '_blank'}
          rel={network.url?.startsWith('mailto:') ? undefined : 'noreferrer'}
        >
          <i className={network.className}></i>
        </a>
      </li>
    );
  });

  return (
    <header id="home">
      <nav id="nav-wrap" className={navOpaque ? 'opaque' : undefined}>
        <a className="mobile-btn" href="#nav-wrap" title="Show navigation">
          Show navigation
        </a>
        <a className="mobile-btn" href="#home" title="Hide navigation">
          Hide navigation
        </a>

        <ul id="nav" className="nav">
          {NAV_ITEMS.map((item) => (
            <li
              key={item.id}
              className={activeSection === item.id ? 'current' : undefined}
            >
              <a className="smoothscroll" href={`#${item.id}`}>
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="row banner">
        <div className="banner-text">
          <h1 className="responsive-headline">{name}</h1>
          <p className="hero-kicker">
            {[occupation, city].filter(Boolean).join(' \u00b7 ')}
          </p>
          {headline ? <h3>{headline}</h3> : null}
          {/* Hero subheadline parked for now. The copy still lives in
              main.json under "subheadline"; uncomment to bring it back. */}
          {/* {subheadline ? <p className="hero-sub">{subheadline}</p> : null} */}
          {description ? <p className="hero-stack">{description}</p> : null}

          <div className="hero-ctas">
            <a className="button hero-secondary smoothscroll" href="#portfolio">
              <i className="fa fa-arrow-down"></i>See the work
            </a>
            <a
              className="button hero-tertiary"
              href="https://github.com/Tony04031234"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fa fa-github"></i>GitHub
            </a>
          </div>

          {proofStrip.length > 0 && (
            <dl className="hero-proof">
              {proofStrip.map((item) => (
                <div className="hero-proof-item" key={item.label}>
                  <dt className="hero-proof-value">{item.value}</dt>
                  <dd className="hero-proof-label">{item.label}</dd>
                </div>
              ))}
            </dl>
          )}
          <hr />
          {networks.length > 0 ? <ul className="social">{networks}</ul> : null}
        </div>
      </div>

      <p
        className={`scrolldown${scrollCueHidden ? ' scrolldown--hidden' : ''}`}
      >
        <a
          className="smoothscroll"
          href="#portfolio"
          aria-label="Scroll to featured work"
          tabIndex={scrollCueHidden ? -1 : 0}
        >
          <i className="icon-down-circle" aria-hidden="true"></i>
        </a>
      </p>
    </header>
  );
};

export default Header;
